<template>
  <div>
    <div
      class="line-message-container p-3 mt-3"
      id="lineMessageContainer"
      :class="{
        'input--error':
          isSubmit &&
          (errorMessageTemplates || checkMessageTemplatesLineErrorExist),
      }"
    >
      <h5>Line messages</h5>
      <div class="d-flex flex-row">
        <div class="form-check form-check-inline pr-15">
          <input
            class="form-check-input mr-2"
            :id="'plainText_' + identifier"
            type="checkbox"
            @change="changeTextCheckBox"
            ref="chkText"
            v-model="isTextMessageShown"
            :disabled="isDisabled"
          />
          <label class="form-check-label" :for="'plainText_' + identifier"
            >Plain text</label
          >
        </div>
        <div class="form-check form-check-inline pr-15">
          <input
            class="form-check-input mr-2"
            :id="'image_' + identifier"
            type="checkbox"
            @change="changeImageCheckBox"
            ref="chkImage"
            v-model="isImageMessageShown"
            :disabled="isDisabled"
          />
          <label class="form-check-label" :for="'image_' + identifier"
            >Image</label
          >
        </div>
        <div class="form-check form-check-inline">
          <input
            class="form-check-input mr-2"
            :id="'video_' + identifier"
            type="checkbox"
            @change="changeVideoCheckBox"
            ref="chkVideo"
            v-model="isVideoMessageShown"
            :disabled="isDisabled"
          />
          <label class="form-check-label" :for="'video_' + identifier"
            >Video</label
          >
        </div>
      </div>
      <div class="mt-5" v-show="isTextMessageShown">
        <p class="font-weight-bold mb-0">Text message</p>
        <CommonMessageTemplate
          v-model="messageTemplateLine"
          :trigger-type="triggerType"
          :has-top40="hasTop40"
          :is-submit="isSubmit"
          :is-broadcast="isBroadcast"
          :is-remark-shown="isRemarkShown"
          :is-disabled="isDisabled"
          label="Message template"
          :data-id="dataId"
          :identifier="identifier + '_lineMessage'"
          :error-message-template="errorMessageTemplates"
        />
      </div>
      <div class="row mt-7" v-show="isImageMessageShown">
        <p class="font-weight-bold mb-0">Image message</p>
        <div class="col pt-1">
          <label :for="'imageMessage_' + identifier">
            Image (JPEG or PNG)<span class="required">*</span></label
          >
          <input
            :id="'imageMessage_' + identifier"
            :disabled="isImageLoading || isDisabled"
            type="file"
            @change="uploadImage"
            ref="imageFile"
            class="form-control"
            accept="image/png, image/jpeg"
            :class="{
              'input--error':
                isSubmit &&
                messageTemplate &&
                !messageTemplate.imageRelativePath &&
                error.errorImageRelativePath,
            }"
          />
          <small class="warning-text"
            >Image ratio is 1:1 <br />
            Max file size is 10 MB<br />
            Max image size is 1024 x 1024 pixels</small
          >
          <div
            v-if="
              isSubmit &&
              messageTemplate &&
              !messageTemplate.imageRelativePath &&
              error.errorImageRelativePath
            "
            class="text-error"
          >
            {{ error.errorImageRelativePath.message }}
          </div>
          <div v-if="uploadImageErrors.length > 0" class="text-error">
            <div
              v-for="error in uploadImageErrors"
              style="list-style-type: none"
              :key="error"
            >
              {{ error }}
            </div>
          </div>
          <div class="row">
            <div class="col text-center" v-if="isImageLoading">
              <FileLoading />
            </div>
            <div
              v-else-if="
                messageTemplate &&
                messageTemplate.imageRelativePathUrl &&
                !isImageLoading
              "
              class="col-6"
            >
              <img
                :src="messageTemplate.imageRelativePathUrl"
                class="preview-image"
                alt="preview uploaded message"
              />
            </div>
          </div>
        </div>
        <div class="col pt-1">
          <label :for="'targetUrl_' + identifier">
            Target URL<span class="required">*</span></label
          >
          <input
            :id="'targetUrl_' + identifier"
            type="text"
            v-model="targetUrl"
            class="form-control"
            :class="{
              'input--error':
                isSubmit &&
                messageTemplate &&
                !messageTemplate.targetUrl &&
                error.errorTargetUrl,
            }"
            :disabled="isDisabled"
          />
          <div
            v-if="
              isSubmit &&
              messageTemplate &&
              !messageTemplate.targetUrl &&
              error.errorTargetUrl
            "
            class="text-error"
          >
            {{ error.errorTargetUrl.message }}
          </div>
        </div>
      </div>
      <div class="row mt-7" v-show="isVideoMessageShown">
        <p class="font-weight-bold mb-0">Video message</p>
        <div class="col pt-1">
          <label :for="'previewImage_' + identifier"
            >Preview image (JPEG or PNG)<span class="required">*</span></label
          >
          <div class="row d-flex align-items-center">
            <div class="col-8">
              <input
                :id="'previewImage_' + identifier"
                :disabled="isVideoImageLoading || isDisabled"
                type="file"
                @change="uploadVideoImage"
                ref="videoImageFile"
                class="form-control"
                accept="image/*"
                :class="{
                  'input--error':
                    isSubmit &&
                    messageTemplate &&
                    !messageTemplate.previewImageRelativePath &&
                    error.errorPreviewImageRelativePath,
                }"
              />
            </div>
            <div class="col-4 pl-0">
              <small class="warning-text"> Max file size is 1 MB</small>
            </div>
          </div>
          <div v-if="uploadVideoImageErrors.length > 0" class="text-error pt-1">
            <div
              v-for="error in uploadVideoImageErrors"
              style="list-style-type: none"
              :key="error"
            >
              {{ error }}
            </div>
          </div>
          <div
            v-if="
              isSubmit &&
              messageTemplate &&
              !messageTemplate.previewImageRelativePath &&
              error.errorPreviewImageRelativePath
            "
            class="text-error"
          >
            {{ error.errorPreviewImageRelativePath.message }}
          </div>
          <div class="row">
            <div class="col text-center" v-if="isVideoImageLoading">
              <FileLoading />
            </div>
            <div
              v-else-if="
                messageTemplate &&
                messageTemplate.previewImageRelativePathUrl &&
                !isVideoImageLoading
              "
              class="col-6"
            >
              <img
                :src="messageTemplate.previewImageRelativePathUrl"
                class="preview-image"
                alt="preview uploaded video message"
              />
            </div>
          </div>
        </div>
        <div class="col pt-1">
          <label :for="'previewVideo_' + identifier"
            >Video<span class="required">*</span></label
          >
          <div class="row">
            <div class="col-8">
              <input
                :id="'previewVideo_' + identifier"
                :disabled="isVideoLoading || isDisabled"
                type="file"
                @change="uploadVideo"
                ref="videoFile"
                class="form-control"
                accept="video/mp4"
                :class="{
                  'input--error':
                    isSubmit &&
                    messageTemplate &&
                    !messageTemplate.videoRelativePath &&
                    error.errorVideoRelativePath,
                }"
              />
            </div>
            <div class="col-4 pl-0 pt-1">
              <small class="warning-text"
                >File type is MP4 <br />
                Max file size is 200 MB</small
              >
            </div>
          </div>
          <div v-if="uploadVideoErrors.length > 0" class="text-error pt-1">
            <div
              v-for="error in uploadVideoErrors"
              style="list-style-type: none"
              :key="error"
            >
              {{ error }}
            </div>
          </div>
          <div
            v-if="
              isSubmit &&
              messageTemplate &&
              !messageTemplate.videoRelativePath &&
              error.errorVideoRelativePath
            "
            class="text-error"
          >
            {{ error.errorVideoRelativePath.message }}
          </div>
          <div class="row">
            <div class="col text-center" v-if="isVideoLoading">
              <FileLoading />
            </div>
            <div
              v-else-if="
                messageTemplate &&
                messageTemplate.videoRelativePathUrl &&
                !isVideoLoading
              "
              class="col-6"
            >
              <!-- //NOSONAR --><video
                class="preview-image"
                :src="messageTemplate.videoRelativePathUrl"
                controls
                width="250"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="isSubmit && errorMessageTemplatesArray.length > 0"
      class="text-error"
    >
      <div v-for="(e, i) in errorMessageTemplatesArray" :key="i">
        <div v-if="+e.fieldName.substring(19) === index + 1" class="text-error">
          {{ e.message }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CommonMessageTemplate from "components/MessageTemplate/CommonMessageTemplate.vue";
import FileLoading from "components/Loading/FileLoading.vue";
import PresignedUploadUrlService from "services/api/presignedUploadUrl.service";

export default {
  name: "LineMessageTemplate",
  components: { FileLoading, CommonMessageTemplate },
  props: {
    triggerType: {
      type: String,
      default: () => null,
    },
    hasTop40: {
      type: Boolean,
      default: () => false,
    },
    messageTemplate: {
      type: Object,
      default: () => {},
    },
    errorMessageTemplates: {
      type: Object,
      default: () => {},
    },
    errorMessageTemplatesArray: {
      type: Array,
      default: () => [],
    },
    index: {
      type: Number,
      default: () => null,
    },
    error: {
      type: Object,
      default: () => {},
    },
    isSubmit: {
      type: Boolean,
      default: () => false,
    },
    isBroadcast: {
      type: Boolean,
      default: () => false,
    },
    dataId: {
      type: String,
      default: () => "",
    },
    identifier: {
      type: String,
      default: () => "",
    },
    isRemarkShown: {
      type: Boolean,
      default: () => false,
    },
    isDisabled: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      isTextMessageShown: false,
      isImageMessageShown: false,
      isVideoMessageShown: false,
      isImageLoading: false,
      uploadedImage: {
        size: "",
        height: "",
        width: "",
      },
      imageFile: null,
      uploadImageErrors: [],

      isVideoImageLoading: false,
      uploadedVideoImage: {
        size: "",
        height: "",
        width: "",
      },
      videoImageFile: null,
      uploadVideoImageErrors: [],

      isVideoLoading: false,
      uploadedVideo: {
        size: "",
      },
      videoFile: null,
      uploadVideoErrors: [],
    };
  },
  mounted() {
    this.checkTextCheckboxCondition(this.messageTemplate?.messageTemplateLine);
    this.checkImageCheckboxCondition(
      this.messageTemplate?.imageRelativePath || this.messageTemplate?.targetUrl
    );
    this.checkVideoCheckboxCondition(
      this.messageTemplate?.previewImageRelativePath ||
        this.messageTemplate?.videoRelativePath
    );
  },
  computed: {
    messageTemplateLine: {
      get: function () {
        return this.messageTemplate?.messageTemplateLine;
      },
      set: function (newValue) {
        if (this.messageTemplate) {
          this.messageTemplate.messageTemplateLine = newValue;
        }
      },
    },
    targetUrl: {
      get: function () {
        return this.messageTemplate?.targetUrl;
      },
      set: function (newValue) {
        if (this.messageTemplate) {
          this.messageTemplate.targetUrl = newValue;
        }
      },
    },

    checkMessageTemplatesLineErrorExist() {
      return this.errorMessageTemplatesArray?.find(
        (e) => +e.fieldName.substring(19) === this.index + 1
      );
    },
  },
  watch: {
    messageTemplate: function () {
      this.checkTextCheckboxCondition(
        this.messageTemplate?.messageTemplateLine
      );
      this.checkImageCheckboxCondition(
        this.messageTemplate?.imageRelativePath ||
          this.messageTemplate?.targetUrl
      );
      this.checkVideoCheckboxCondition(
        this.messageTemplate?.previewImageRelativePath ||
          this.messageTemplate?.videoRelativePath
      );
    },
  },
  methods: {
    uploadImage() {
      this.messageTemplate.imageRelativePathUrl = null;
      this.messageTemplate.imageRelativePath = null;
      this.uploadImageErrors.splice(0, this.uploadImageErrors.length);
      this.imageFile = this.$refs.imageFile.files[0];
      if (this.imageFile) {
        if (this.isFileSizeExceeded(this.imageFile, 10000000)) {
          this.uploadImageErrors.push("The file size exceeds 10MB");
        }
        let imgReader = new FileReader();
        imgReader.readAsDataURL(this.imageFile);
        imgReader.onload = (evt) => {
          let img = new Image();
          img.onload = async () => {
            this.uploadedImage.width = img.width;
            this.uploadedImage.height = img.height;
            if (this.notValidImageRatio(this.uploadedImage)) {
              this.uploadImageErrors.push("The image ratio must be 1:1");
            }
            if (this.isMaxPixelExceeded(this.uploadedImage, 1024)) {
              this.uploadImageErrors.push(
                "The width or height of the image exceeds max (1024 pixels)"
              );
            }
            if (this.notHaveError(this.uploadImageErrors)) {
              this.messageTemplate.imageRelativePathUrl = URL.createObjectURL(
                this.imageFile
              );
              this.isImageLoading = true;
              await this.getPresignURL(this.imageFile);
            }
          };
          img.src = evt.target.result;
        };
      }
    },
    async uploadVideoImage() {
      this.messageTemplate.previewImageRelativePathUrl = null;
      this.messageTemplate.previewImageRelativePath = null;
      this.uploadVideoImageErrors.splice(0, this.uploadVideoImageErrors.length);
      this.videoImageFile = this.$refs.videoImageFile.files[0];
      if (this.videoImageFile) {
        if (this.isFileSizeExceeded(this.videoImageFile, 1000000)) {
          this.uploadVideoImageErrors.push("The file size exceeds 1MB");
        }
        let vdoImgReader = new FileReader();
        vdoImgReader.readAsDataURL(this.videoImageFile);
        vdoImgReader.onload = (evt) => {
          let vdoImg = new Image();
          vdoImg.onload = async () => {
            if (this.notHaveError(this.uploadVideoImageErrors)) {
              this.messageTemplate.previewImageRelativePathUrl =
                URL.createObjectURL(this.videoImageFile);
              this.isVideoImageLoading = true;
              await this.getPresignURL(this.videoImageFile);
            }
          };
          vdoImg.src = evt.target.result;
        };
      }
    },
    async uploadVideo() {
      this.messageTemplate.videoRelativePathUrl = null;
      this.messageTemplate.videoRelativePath = null;
      this.uploadVideoErrors.splice(0, this.uploadVideoErrors.length);
      this.videoFile = this.$refs.videoFile.files[0];
      if (this.videoFile) {
        if (this.isFileSizeExceeded(this.videoFile, 200000000)) {
          this.uploadVideoErrors.push("The file size exceeds 200MB");
        }
        if (this.notHaveError(this.uploadVideoErrors)) {
          this.messageTemplate.videoRelativePathUrl = URL.createObjectURL(
            this.videoFile
          );
          this.isVideoLoading = true;
          await this.getPresignURL(this.videoFile);
        }
      }
    },
    notValidImageRatio(image) {
      return image.width / image.height !== 1;
    },
    isFileSizeExceeded(file, byteSize) {
      return file.size > byteSize;
    },
    notHaveError(errors) {
      return errors.length === 0;
    },
    isMaxPixelExceeded(image, pixel) {
      return image.width > pixel || image.height > pixel;
    },
    async getPresignURL(file) {
      await PresignedUploadUrlService.getS3SignUrl("lineMessage", file)
        .then((res) => {
          const data = res.data;
          if (file === this.imageFile) {
            this.messageTemplate.imageRelativePath = data.key;
          } else if (file === this.videoImageFile) {
            this.messageTemplate.previewImageRelativePath = data.key;
          } else if (file === this.videoFile) {
            this.messageTemplate.videoRelativePath = data.key;
          }
          if (data.status === "failure") {
            if (file === this.imageFile) {
              this.isImageLoading = false;
            } else if (file === this.videoImageFile) {
              this.isVideoImageLoading = false;
            } else if (file === this.videoFile) {
              this.isVideoLoading = false;
            }
            this.showDialog = true;
            this.message = data.message;
          } else {
            this.updatePhotoToS3(data.presignedUrl, file);
          }
        })
        .catch((err) => {
          console.log("Error: ", err.response);
        });
    },
    async updatePhotoToS3(url, file) {
      await PresignedUploadUrlService.pushProfilePhotoToS3(url, file);
      if (file === this.imageFile) {
        this.$refs.imageFile.value = null;
        this.isImageLoading = false;
      } else if (file === this.videoImageFile) {
        this.$refs.videoImageFile.value = null;
        this.isVideoImageLoading = false;
      } else if (file === this.videoFile) {
        this.$refs.videoFile.value = null;
        this.isVideoLoading = false;
      }
    },
    changeTextCheckBox() {
      this.checkTextCheckboxCondition(this.$refs.chkText.checked);
    },
    changeImageCheckBox() {
      this.checkImageCheckboxCondition(this.$refs.chkImage.checked);
    },
    changeVideoCheckBox() {
      this.checkVideoCheckboxCondition(this.$refs.chkVideo.checked);
    },
    checkTextCheckboxCondition(condition) {
      if (this.messageTemplate) {
        if (condition) {
          this.isTextMessageShown = true;
        } else {
          this.isTextMessageShown = false;
          this.messageTemplate.messageTemplateLine = null;
        }
      }
    },
    checkImageCheckboxCondition(condition) {
      if (this.messageTemplate) {
        if (condition) {
          this.isImageMessageShown = true;
        } else {
          this.isImageMessageShown = false;
          this.messageTemplate.imageRelativePath = null;
          this.messageTemplate.imageRelativePathUrl = null;
          this.messageTemplate.targetUrl = null;
          this.$refs.imageFile.value = null;
          this.$emit("update-preview-image", null);
        }
      }
    },
    checkVideoCheckboxCondition(condition) {
      if (this.messageTemplate) {
        if (condition) {
          this.isVideoMessageShown = true;
        } else {
          this.isVideoMessageShown = false;
          this.messageTemplate.previewImageRelativePath = null;
          this.messageTemplate.previewImageRelativePathUrl = null;
          this.messageTemplate.videoRelativePath = null;
          this.messageTemplate.videoRelativePathUrl = null;
          this.$refs.videoImageFile.value = null;
          this.$refs.videoFile.value = null;
          this.$emit("update-preview-video-image", null);
          this.$emit("update-preview-video", null);
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.line-message-container {
  border: 2px solid #d2d0d0;
  border-radius: 5px;
}

.warning-text {
  color: #2e2ecc;
}

.text-error {
  color: red;
  font-size: 12px;
}

.preview-image {
  max-width: 300px;
  height: auto;
}
</style>
